/**
 * Shared wire primitives. Every money value is integer cents, every event date `YYYY-MM-DD`, every
 * time of day `HH:mm` (venue-local), every instant ISO 8601 with offset, every id a uuid.
 */
import { z } from 'zod';
export const Id = z.uuid();
/** Integer cents. Signed: a discount or refund line may be negative. */
export const Cents = z.number().int().min(-1_000_000_000).max(1_000_000_000);
export const NonNegativeCents = z.number().int().min(0).max(1_000_000_000);
/** A percentage as a number of percent: 6.625 means 6.625 %. */
export const Percent = z.number().min(0).max(100);
/** An event date, `YYYY-MM-DD`, in the venue's timezone. */
export const DateString = z.iso.date();
/**
 * A venue-local time of day, `HH:mm` (00:00–23:59). Postgres `time` columns read back as `HH:mm:ss`;
 * that form is accepted and normalised to `HH:mm`.
 */
export const TimeHHmm = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d(:00)?$/, 'Expected a time as HH:mm')
    .transform((s) => s.slice(0, 5));
/**
 * An instant, ISO 8601 with offset (`Z` included). Server code may hand a `Date` (Drizzle returns
 * them for timestamptz); parsing turns it into an ISO string, so the wire type is always `string`.
 */
export const Instant = z.union([
    z.iso.datetime({ offset: true }),
    z.date().transform((d) => d.toISOString()),
]);
/** A half-open interval of instants `[startsAt, endsAt)`. */
export const InstantRange = z.object({ startsAt: Instant, endsAt: Instant });
export const Email = z.string().trim().pipe(z.email().max(254));
/** E.164, e.g. `+19732215553`. */
/** ISO 4217 currency code; every `*_cents` amount of a venue is in its currency. */
export const Currency = z.string().regex(/^[A-Z]{3}$/, 'Expected an ISO 4217 code like USD');
export const PhoneE164 = z.string().regex(/^\+[1-9]\d{7,14}$/, 'Expected a phone number in E.164 form');
export const Weekday = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
/** Opening window on one day. `close` is later than `open` on the same day. */
export const TimeRange = z.object({ open: TimeHHmm, close: TimeHHmm });
/** Every weekday present; an empty array means no hours that day. */
export const HoursByWeekday = z.record(Weekday, z.array(TimeRange));
/** Every weekday present; 0 means no minimum. */
export const CentsByWeekday = z.record(Weekday, NonNegativeCents);
/** Short url-safe identifier used for natural keys (rooms, products, packages, templates…). */
export const Slug = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/, 'Expected lowercase letters, digits, - or _');
/** Who did something: a guest, the concierge, the system, or a staff user (`staff:<uuid>`). */
export const Actor = z
    .string()
    .regex(/^(guest|concierge|system|staff:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/);
export const Timestamps = { createdAt: Instant, updatedAt: Instant };
/** `{ items: T[] }` — the shape of every list response. */
export const listOf = (item) => z.object({ items: z.array(item) });
export const IdParams = z.object({ id: Id });
// ── Errors ────────────────────────────────────────────────────────────────
/** Codes the platform and the phase-0 features produce. Feature code may add more (SCREAMING_SNAKE). */
export const ErrorCodes = {
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    BAD_REQUEST: 'BAD_REQUEST',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    FORBIDDEN: 'FORBIDDEN',
    CSRF_FAILED: 'CSRF_FAILED',
    NOT_FOUND: 'NOT_FOUND',
    /** No venue matches the request's Host/Origin, embed key or token. */
    VENUE_NOT_FOUND: 'VENUE_NOT_FOUND',
    CONFLICT: 'CONFLICT',
    HOLD_CONFLICT: 'HOLD_CONFLICT',
    HOLD_LIMIT: 'HOLD_LIMIT',
    // Reserve (phase 3, 12 §2): agreement, deposit, booking, Resy.
    /** 409: the hold this payment or signature was for is no longer active (expired, released, taken). */
    HOLD_GONE: 'HOLD_GONE',
    /** 409: checkout asked for before the current agreement is signed. */
    AGREEMENT_NOT_SIGNED: 'AGREEMENT_NOT_SIGNED',
    /** 409: a newer quote voided the agreement the guest is acting on; they read and sign again. */
    AGREEMENT_SUPERSEDED: 'AGREEMENT_SUPERSEDED',
    /** 409: no approved agreement-template version; nothing is rendered or sent. */
    TEMPLATE_NOT_APPROVED: 'TEMPLATE_NOT_APPROVED',
    /** 402: a charge was declined or needs the payer (details carry the `Payment`). */
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    /** 409: the venue has no connected Stripe account (Settings → Payments → Connect your Stripe). */
    PAYMENTS_NOT_CONNECTED: 'PAYMENTS_NOT_CONNECTED',
    /** 400: a payment webhook whose signature does not verify over the raw body. */
    WEBHOOK_SIGNATURE: 'WEBHOOK_SIGNATURE',
    /** 503: Resy could not be read (no credentials, login refused, timeout); never a 500. */
    RESY_UNAVAILABLE: 'RESY_UNAVAILABLE',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
    RATE_LIMITED: 'RATE_LIMITED',
    INTERNAL: 'INTERNAL',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
    /** `POST /g/concierge` streams it (as `error { code }`) when the agent is off: the drawer falls back to ask-us. */
    CONCIERGE_OFF: 'CONCIERGE_OFF',
    // Phase 2 (11 §2–§4). The first four are `MessageSender` refusals (a policy reason, never a throw);
    // the next three are the send policy's own refusals the loop hands the model; the rest are HTTP.
    /** The channel has no consent that covers this message (SMS without `granted`, outside customer care). */
    CONSENT: 'CONSENT',
    /** Inside quiet hours, Shabbat or the TCPA window and the message could not be queued. */
    QUIET_HOURS: 'QUIET_HOURS',
    /** The guest revoked the channel (STOP, unsubscribe, bounce, complaint, staff). */
    REVOKED: 'REVOKED',
    /** The contact has no address on the channel (no email, no mobile number). */
    NO_ADDRESS: 'NO_ADDRESS',
    /** A follow-up ceiling: too many unanswered touches, or the next gap has not passed. */
    CEILING: 'CEILING',
    /** A touch that cites no fact any earlier outbound touch has not already cited. */
    NOTHING_NEW: 'NOTHING_NEW',
    /** A touch whose body names nothing recorded about this lead (personalisation rule). */
    NOT_PERSONAL: 'NOT_PERSONAL',
    /** Deciding an approval after `expiresAt` (409). */
    APPROVAL_EXPIRED: 'APPROVAL_EXPIRED',
    /** SMS consent refused at capture: the number is a landline (carrier lookup). */
    LANDLINE: 'LANDLINE',
    // Phase 4 (13 §2): tours, the calendar, web push.
    /** 409: the tour slot was taken (or stopped being offered) since it was read; read slots again. */
    SLOT_TAKEN: 'SLOT_TAKEN',
    /** 403: a guest or the concierge tried to book a tour type staff book only (`selfServe` false). */
    TOUR_NOT_SELF_SERVE: 'TOUR_NOT_SELF_SERVE',
    /** 503: the connected calendar could not be reached; shown on the connection, never fails a booking. */
    CALENDAR_UNAVAILABLE: 'CALENDAR_UNAVAILABLE',
    /** 503: web push is not configured on this server (no VAPID keys). */
    PUSH_UNSUPPORTED: 'PUSH_UNSUPPORTED',
    /** 503: Dialpad could not be reached (connect, disconnect, a test text); nothing changed. */
    DIALPAD_UNAVAILABLE: 'DIALPAD_UNAVAILABLE',
};
export const ValidationIssue = z.object({
    path: z.array(z.union([z.string(), z.number()])),
    message: z.string(),
    code: z.string(),
});
/** Every non-2xx response body. `details` is `ValidationIssue[]` for VALIDATION_FAILED. */
export const ErrorBody = z.object({
    error: z.object({
        code: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
// ── Media ─────────────────────────────────────────────────────────────────
/**
 * A photo or floor plan as stored: either an uploaded file (`key`, served at `/files/:key`) or a
 * remote URL (seed photos from beccanj.com until Daniel uploads his own).
 */
export const PhotoInput = z
    .object({
    key: z.string().min(1).max(200).nullish(),
    url: z.url().nullish(),
    alt: z.string().max(300).default(''),
})
    .refine((p) => Boolean(p.key) !== Boolean(p.url), { message: 'Give exactly one of key or url' });
/** A photo on the wire: always has a resolved `url`. */
export const Photo = z.object({
    key: z.string().nullable(),
    url: z.string(),
    alt: z.string(),
});
//# sourceMappingURL=common.js.map